

import React, { useState, useEffect } from 'react';
import {
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import MDButton from "components/MDButton";
import ReactQuill from 'react-quill';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import colors from "assets/theme/base/colors";
import { useAuth } from '../../auth/Auth';
import {
  appendSignatureToContent,
  fetchEmailSignature,
} from 'utils/emailSignature';

// Import AI icon component
const AIIcon = (props) => (
  <img 
    src="https://res.cloudinary.com/dhtfyxzcp/image/upload/t_Thumbnail/v1741009794/Untitled_design_xwq8o1.svg"
    alt="AI"
    style={{ 
      width: 24,
      height: 24,
      ...props.style
    }}
  />
);

const EmailModal = ({
  open,
  onClose,
  newEmail,
  setNewEmail,
  handleSend,
  isGenerating,
  isSending, // Change this from loading to isSending
  errorMessage,
  emailContext,
  setEmailContext,
  handleGenerateEmail,
  showCc,
  showBcc,
  toggleCc,
  toggleBcc,
  handleQuillChange,
  modules,
  formats,
  attachments = [],
  setEmailAttachments,
}) => {
  const { axiosInstance } = useAuth();
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [localEmailContext, setLocalEmailContext] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [isGeneratingLocal, setIsGeneratingLocal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [signatureHtml, setSignatureHtml] = useState('');

  // Initialize localEmailContext when emailContext prop changes
  useEffect(() => {
    setLocalEmailContext(typeof emailContext === 'string' ? emailContext : '');
  }, [emailContext]);

  useEffect(() => {
    if (!open) {
      setValidationErrors({});
    }
  }, [open]);

  // Load configured signature and append when composing (CRM EmailModal)
  useEffect(() => {
    if (!open || !axiosInstance || typeof setNewEmail !== 'function') return undefined;

    let cancelled = false;
    (async () => {
      const signature = await fetchEmailSignature(axiosInstance);
      if (cancelled) return;
      setSignatureHtml(signature || '');
      if (!signature) return;

      setNewEmail((prev) => {
        const nextContent = appendSignatureToContent(prev?.content || '', signature);
        if (nextContent === (prev?.content || '')) return prev;
        return { ...prev, content: nextContent };
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [open, axiosInstance, setNewEmail]);

  const hasEmailContent = (content) => (
    (content || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim().length > 0
  );

  const handleValidatedSend = () => {
    const errors = {};
    if (!newEmail.to?.trim()) errors.to = 'Recipient is required';
    if (!newEmail.subject?.trim()) errors.subject = 'Subject is required';
    if (!hasEmailContent(newEmail.content)) errors.content = 'Email content is required';

    setValidationErrors(errors);
    if (Object.keys(errors).length === 0) {
      handleSend();
    }
  };

  // Function to remove an attachment
  const handleRemoveAttachment = (index) => {
    setEmailAttachments(attachments.filter((_, i) => i !== index));
  };

  // Function to add attachments
  const handleAddAttachment = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      // Convert files to attachment format
      const newAttachments = newFiles.map(file => ({
        filename: file.name,
        size: file.size,
        file: file // Store the actual file object
      }));
      setEmailAttachments([...attachments, ...newAttachments]);
    }
  };

  const handleOpenAIDialog = () => {
    setOpenAIDialog(true);
    setGenerationError('');
  };

  const handleCloseAIDialog = () => {
    setOpenAIDialog(false);
    setGenerationError('');
  };

  const handleAIGenerate = async () => {
    const context = typeof localEmailContext === 'string' ? localEmailContext : '';
    if (context && context.trim().length > 0 && axiosInstance) {
      try {
        setIsGeneratingLocal(true);
        
        // Call the generate email API endpoint directly
        const response = await axiosInstance.post('/generate/email', {
          context: context,
          signature: signatureHtml || undefined,
        });

        if (response.data) {
          const { subject, content } = response.data;
          setNewEmail(prev => ({
            ...prev,
            subject: subject || prev.subject,
            // Backend already appends signature when provided
            content: content || prev.content,
          }));
          handleCloseAIDialog();
        }
      } catch (error) {
        console.error('Error generating email:', error);
        setGenerationError(error.response?.data?.detail || 'Failed to generate email content');
      } finally {
        setIsGeneratingLocal(false);
      }
    }
  };

  const handleContextChange = (e) => {
    const newValue = e?.target?.value ?? '';
    setLocalEmailContext(newValue);
    if (typeof setEmailContext === 'function') {
      setEmailContext(newValue);
    }
  };

  const isGenerateDisabled = () => {
    const context = typeof localEmailContext === 'string' ? localEmailContext : '';
    return !context || context.trim().length === 0;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
          },
        }}
      >
        {/* Professional Header */}
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            px: 4,
            py: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Typography 
            variant="h6" 
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            Compose Email
          </Typography>
        </Box>

        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            margin="normal"
            label="To"
            required
            value={newEmail.to}
            onChange={(e) => {
              setNewEmail({ ...newEmail, to: e.target.value });
              if (validationErrors.to) {
                setValidationErrors((previous) => ({ ...previous, to: undefined }));
              }
            }}
            error={Boolean(validationErrors.to)}
            helperText={validationErrors.to}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
            <Link 
              component="button" 
              variant="body2" 
              onClick={toggleCc}
              sx={{ color: '#1565C8' }}
            >
              Cc
            </Link>
            <Link 
              component="button" 
              variant="body2" 
              onClick={toggleBcc}
              sx={{ color: '#1565C8' }}
            >
              Bcc
            </Link>
          </Box>
          {showCc && (
            <TextField
              label="CC"
              value={newEmail.cc}
              onChange={(e) => setNewEmail({ ...newEmail, cc: e.target.value })}
              fullWidth
              variant="outlined"
              margin="normal"
            />
          )}
          {showBcc && (
            <TextField
              fullWidth
              margin="normal"
              label="Bcc"
              value={newEmail.bcc}
              onChange={(e) => setNewEmail({ ...newEmail, bcc: e.target.value })}
            />
          )}
          <TextField
            fullWidth
            margin="normal"
            label="Subject"
            required
            value={newEmail.subject}
            onChange={(e) => {
              setNewEmail({ ...newEmail, subject: e.target.value });
              if (validationErrors.subject) {
                setValidationErrors((previous) => ({ ...previous, subject: undefined }));
              }
            }}
            error={Boolean(validationErrors.subject)}
            helperText={validationErrors.subject}
          />
          
          {/* Email Body with AI Button */}
          <Box
            sx={{
              position: 'relative',
              mt: 2,
              mb: 4,
              border: validationErrors.content ? '1px solid #d32f2f' : 'none',
              borderRadius: validationErrors.content ? '4px' : 0,
            }}
          >
            <ReactQuill
              value={newEmail.content}
              onChange={(content) => {
                handleQuillChange(content);
                if (validationErrors.content && hasEmailContent(content)) {
                  setValidationErrors((previous) => ({ ...previous, content: undefined }));
                }
              }}
              modules={modules}
              formats={formats}
              style={{ height: '300px', marginBottom: '50px' }}
            />
            <Tooltip title="Generate with AI" placement="top">
              <IconButton
                onClick={handleOpenAIDialog}
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8,
                  color: '#1565C8',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 158, 219, 0.04)',
                  }
                }}
                disabled={isGenerating}
              >
                <AIIcon />
              </IconButton>
            </Tooltip>
            {isGenerating && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress sx={{ color: '#1565C8' }} size={24} />
              </Box>
            )}
            {validationErrors.content && (
              <Typography color="error" variant="caption" sx={{ display: 'block', px: 1.5, pb: 1 }}>
                {validationErrors.content}
              </Typography>
            )}
          </Box>

          {/* Attachment Section - Moved below the editor */}
          <Box sx={{ mt: 2 }}>
            <input
              accept="*/*"
              style={{ display: 'none' }}
              id="add-attachment-button"
              type="file"
              multiple
              onChange={handleAddAttachment}
            />
            <label htmlFor="add-attachment-button">
              <MDButton
                component="span"
                variant="outlined"
                color="info"
                startIcon={<AttachFileIcon />}
              >
                Add Attachment
              </MDButton>
            </label>
          </Box>

          {/* Display Attachments */}
          {attachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Attachments:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {attachments.map((attachment, index) => (
                  <Chip
                    key={index}
                    icon={<AttachFileIcon />}
                    label={`${attachment.filename} (${(attachment.size / 1024).toFixed(2)} KB)`}
                    variant="outlined"
                    deleteIcon={<CloseIcon />}
                    onDelete={() => handleRemoveAttachment(index)}
                    sx={{ 
                      borderColor: '#1565C8',
                      '& .MuiChip-icon': { color: '#1565C8' },
                      '& .MuiChip-deleteIcon': { 
                        color: '#1565C8',
                        '&:hover': { color: '#0E4FA3' }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {errorMessage && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <MDButton
            onClick={onClose}
            variant="outlined"
            color="secondary"
            disabled={isSending}  // Disable cancel button while sending
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleValidatedSend}
            disabled={isSending}
            variant="gradient"
            color="info"
            sx={{ position: 'relative', minWidth: '120px' }}
          >
            {isSending ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '100%'
              }}>
                <CircularProgress
                  size={20}
                  sx={{
                    color: 'white',
                    mr: 1
                  }}
                />
                Sending...
              </Box>
            ) : (
              'Send'
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog
        open={openAIDialog}
        onClose={handleCloseAIDialog}
        PaperProps={{
          style: { borderRadius: '12px', padding: '16px', minWidth: '400px' },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1565C8',
          }}
        >
          Generate Email with AI
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Context"
            type="text"
            fullWidth
            multiline
            rows={6}
            value={localEmailContext}
            onChange={handleContextChange}
            placeholder="Describe the purpose and key points of your email..."
            variant="outlined"
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#1565C8' },
                '&:hover fieldset': { borderColor: '#0E4FA3' },
                '&.Mui-focused fieldset': { borderColor: '#1565C8' },
              },
            }}
          />
          {generationError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {generationError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <MDButton
            onClick={handleCloseAIDialog}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleAIGenerate}
            disabled={isGenerateDisabled() || isGeneratingLocal}
            variant="gradient"
            color="info"
          >
            {isGeneratingLocal ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Generate'
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailModal;